import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from '../router';

import { ApiError } from '../api';
import { useAuth } from '../auth';
import { Brand } from '../components/Brand';
import { Icon } from '../components/Icon';

export function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const { account, login, register } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  if (account) return <Navigate to="/app/inbox" replace />;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true); setError('');
    const data = new FormData(event.currentTarget);
    try {
      if (mode === 'register') await register(String(data.get('displayName')), String(data.get('email')), String(data.get('password')));
      else await login(String(data.get('email')), String(data.get('password')));
      const destination = (location.state as { from?: string } | null)?.from ?? '/app/inbox';
      navigate(destination, { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  }

  return (
    <div className="auth-page">
      <section className="auth-brand-panel">
        <Brand />
        <div><span className="eyebrow">Optional companion account</span><h1>Your texts, on a bigger screen.</h1><p>Account features are separate from local SMS. Nothing is uploaded until you also enable sync on your phone.</p></div>
        <ul><li><Icon name="check_circle" /> Revoke devices any time</li><li><Icon name="check_circle" /> Delete your account and synced content</li><li><Icon name="check_circle" /> No advertising profile</li></ul>
      </section>
      <section className="auth-form-panel">
        <div className="auth-card">
          <Link className="back-link" to="/"><Icon name="arrow_back" /> Back home</Link>
          <h2>{mode === 'register' ? 'Create your account' : 'Welcome back'}</h2>
          <p>{mode === 'register' ? 'You can also create an account inside the Android app.' : 'Open your synced messages and devices.'}</p>
          <form onSubmit={(event) => void submit(event)}>
            {mode === 'register' && <label>Display name<input name="displayName" autoComplete="name" minLength={1} maxLength={80} required /></label>}
            <label>Email address<input name="email" type="email" autoComplete="email" required /></label>
            <label>
              Password
              <input name="password" type="password" autoComplete={mode === 'register' ? 'new-password' : 'current-password'} minLength={mode === 'register' ? 12 : 1} maxLength={128} aria-describedby={mode === 'register' ? 'password-requirements' : undefined} required />
              {mode === 'register' && <small id="password-requirements">At least 12 characters.</small>}
            </label>
            {mode === 'login' && <Link className="forgot-password-link" to="/forgot-password">Forgot password?</Link>}
            {mode === 'register' && <p className="form-disclosure">By creating an account, you agree to the <Link to="/terms">Terms</Link> and acknowledge the <Link to="/privacy">Privacy Policy</Link>.</p>}
            {error && <div className="form-error" role="alert"><Icon name="error" /> {error}</div>}
            <button className="button button-primary button-wide" disabled={busy}>{busy ? 'Please wait…' : mode === 'register' ? 'Create account' : 'Sign in'}</button>
          </form>
          <p className="auth-switch">{mode === 'register' ? 'Already have an account?' : 'New to Messavo?'} <Link to={mode === 'register' ? '/login' : '/register'}>{mode === 'register' ? 'Sign in' : 'Create one'}</Link></p>
        </div>
      </section>
    </div>
  );
}
